<template>
  <v-container id="policy-overview" class="pa-0">
    <v-card flat rounded="0">
      <OnexAlert
        class="mb-4"
        :type="Notifications.error"
        dismissible="false"
        v-if="policyData.billingForm === 'H1'"
      >
        {{ $t('L_your_policy_will_be_terminated', { date: formattedPaymentDueDate }) }}
      </OnexAlert>

      <!-- Success -->
      <OnexAlert
        class="mb-4"
        :type="Notifications.success"
        dismissible
        v-if="addChildSuccess"
        @click:close="addChildSuccess = false"
      >
        {{ $t('L_add_child_success') }}
      </OnexAlert>

      <!-- Info -->
      <OnexAlert class="mb-4" :type="Notifications.info" dismissible="false" v-if="showAlert">
        {{ $t(restrictCodeMessage) }}
        <template #description
          ><span>
            <a :href="'tel:+' + clientCommunicationsPhone">{{ clientCommunicationsPhone }}</a
            >.
          </span></template
        >
      </OnexAlert>

      <ChildRiderAlert v-if="hasCRCPackageForCurrentPolicy" />

      <!-- * TITLE -->
      <template #title>
        <v-row align-content="center">
          <v-col cols="auto">
            <!-- Policy Overview -->
            <h1 role="heading" aria-level="2" class="text-h2">
              {{ __t('L_policy_policy_overview') }}
            </h1>
          </v-col>
          <v-spacer />
          <v-col cols="auto" align-self="center">
            <!-- View Full Policy -->
            <span v-if="hasFullPolicyDocuments">
              <OnexButton
                @click="openDocumentInNewWindow"
                class="hidden-xs mb-2"
                :text="$t('L_policy_view_full_policy')"
                id="view_full_policy_details"
                variant="text"
              >
              </OnexButton>
              <span class="text-h1 text-grey-lighten-1 mx-2 hidden-xs">|</span>
            </span>
            <!-- Policy status -->
            <span class="subheading text-h4 font-weight-medium hidden-xs">{{
              $t('L_policy_status')
            }}</span>
            <!-- Icon & Color  -->
            <span class="text-right">
              <span class="ml-3">
                <v-icon
                  class="pb-1 mt-0 text-h1"
                  size="x-large"
                  :color="policyStatusIconColor"
                  :icon="policyStatusIcon"
                />
                <span class="text-h4 font-weight-medium">
                  {{ policyStatusDescription }}
                </span>
              </span>
            </span>
          </v-col>
        </v-row>
      </template>

      <!-- Policy Overview -->
      <v-card-text>
        <v-container class="dim px-0">
          <!-- View Full Policy -->
          <v-row v-if="policyData.active" class="hidden-sm-and-up text-center">
            <v-col>
              <!-- View Full Policy -->
              <span v-if="hasFullPolicyDocuments">
                <OnexButton
                  @click="openDocumentInNewWindow"
                  :text="$t('L_policy_view_full_policy')"
                  id="view_full_policy_details"
                  variant="text"
                ></OnexButton>
              </span>
            </v-col>
          </v-row>
          <!-- Primary -->
          <v-row>
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center">
              <span class="text-h5 font-weight-bold">{{ $t('L_policy_primary_insured') }}</span>
            </v-col>
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center px-0">
              <span class="text-h5 font-weight-bold">
                {{ __t('L_policy_total_coverage_amount') }}
                <v-tooltip
                  content-class="text-center align-center"
                  location="bottom"
                  :max-width="$vuetify.display.mdAndUp ? '194' : '162'"
                  class="tooltip-overlay"
                >
                  <template #activator="{ props }">
                    <v-icon
                      v-bind="props"
                      class="pb-1 mt-0 text-h2"
                      size="x-large"
                      color="primary"
                      :icon="mdiHelpCircle"
                    />
                  </template>
                  <span role="tooltip" aria-live="polite" id="policy_number_tooltip">
                    {{ __t('L_coverage_amount_tooltip') }}
                  </span>
                </v-tooltip>
              </span>
            </v-col>
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center">
              <h2 class="text-h4 font-weight-regular">{{ policyData.insuredName }}</h2>
            </v-col>
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center">
              <h2 class="text-h4 font-weight-regular">{{ formattedPrimaryCoverage }}</h2>
            </v-col>
          </v-row>
          <!-- spouse -->
          <v-row v-if="spouseName">
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center">
              <h3 class="text-h5 font-weight-bold">{{ $t('L_policy_spouse_insured') }}</h3>
            </v-col>
            <v-col cols="6" sm="6" md="6" lg="6"></v-col>
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center">
              <h2 class="text-h4 font-weight-regular">{{ spouseName }}</h2>
            </v-col>
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center">
              <h2 class="text-h4 font-weight-regular">{{ formattedSpouseCoverage }}</h2>
            </v-col>
          </v-row>
          <!-- children -->
          <v-row v-if="hasChildren">
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center">
              <h3 class="text-h5 font-weight-bold">{{ $t('L_policy_children_insured') }}</h3>
            </v-col>
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center">
              <h2 class="text-h4 font-weight-regular">{{ formattedChildrenCoverage }}</h2>
            </v-col>
            <template v-for="(name, index) in childrenNames" :key="name + index">
              <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center">
                <h2 class="text-h4 font-weight-regular">{{ name }}</h2>
              </v-col>
              <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center"></v-col>
            </template>
            <!-- add child -->
            <v-col
              cols="6"
              sm="6"
              md="6"
              lg="6"
              justify="center"
              class="text-center"
              v-if="globalStore.containsFeature('POL-CHILDADD')"
            >
              <h2 v-if="policy.showAddChildLink">
                <OnexButton
                  @click="addChildOpen = true"
                  variant="text"
                  :text="$t('L_add_a_child')"
                  :prepend-icon="mdiPlusCircleOutline"
                ></OnexButton>
              </h2>
            </v-col>
            <v-col cols="6" sm="6" md="6" lg="6" justify="center" class="text-center"></v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <!-- View Policy Details Expansion Panel -->
      <PolicyOverviewDetailsPanel :policy-data="policyData" />

      <EmbeddedViewer
        title="Life Policy"
        :loading="progressLoading"
        content-type="application/pdf"
        :binary-data="documentContent"
      />
    </v-card>

    <!-- <DialogComponent v-bind="dialogProps" @close-dialog="dialogProps.dialogOpen = false" /> -->
    <!-- <OnexDialog
      type="info"
      v-bind="dialogProps"
      @close-dialog="dialogProps.modelValue = false"
    ></OnexDialog> -->
    <v-dialog
      v-model="addChildOpen"
      :fullscreen="$vuetify.display.xs"
      @close="addChildOpen = false"
      max-width="1080"
      z-index="10000"
    >
      <AddChild
        @close="((addChildOpen = false), (addChildSuccess = true))"
        @cancel="((addChildOpen = false), (addChildSuccess = false))"
      />
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import { formatDate, titleCase, currency } from '@/shared/util/formatters';
import { openInNewWindow } from '@/mixins/openNewWindow';
import { defineComponent, type ComponentOptions } from 'vue';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { mapState, mapStores } from 'pinia';
import { useChildRiderStore } from '@/modules/childrider/store/childrider.store';
import {
  mdiAlert,
  mdiCheckCircle,
  mdiClock,
  mdiExclamation,
  mdiInformation,
  mdiPlusCircle,
  mdiPlusCircleOutline,
  mdiHelpCircle,
} from '@mdi/js';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import ChildRiderAlert from '@/modules/childrider/components/ChildRiderAlert/ChildRiderAlert.vue';
import DialogComponent from '@/components/Dialog/DialogComponent.vue';
import EmbeddedViewer from '@/components/EmbeddedViewer.vue';
import PolicyOverviewDetailsPanel from './PolicyOverviewDetailsPanel.vue';
import AddChild from './PolicyOverviewAddChild.vue';
import { OnexAlert, OnexButton, Notifications } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'PolicyOverview',
  components: {
    PolicyOverviewDetailsPanel,
    ChildRiderAlert,
    EmbeddedViewer,
    DialogComponent,
    AddChild,
    OnexButton,
    OnexAlert,
  },
  mixins: [openInNewWindow],
  props: {
    policyData: {
      type: Object,
      required: true,
    },
    restrictCodeMessage: {
      type: String,
      required: false,
      default: '',
    },
    clientCommunicationsPhone: {
      type: String,
      required: false,
      default: '',
    },
  },
  data: () => ({
    status: false,
    addChildSuccess: false,
    progressLoading: false,
    documentContent: undefined as Uint8Array | ArrayBuffer | undefined,
    dialogProps: {
      dialogOpen: false,
      content: 'content',
      title: 'title',
    },
    addChildOpen: false,
    mdiInformation,
    mdiAlert,
    mdiCheckCircle,
    mdiPlusCircleOutline,
    mdiPlusCircle,
    mdiHelpCircle,
    Notifications,
  }),
  computed: {
    ...mapStores(usePolicyStore, useGlobalStore),
    ...mapState(usePolicyStore, {
      policy: 'currentPolicy',
    }),
    ...mapState(useGlobalStore, {
      getFullPolicyDocument: 'getFullPolicyDocument',
      base64String: 'base64String',
    }),
    ...mapState(useChildRiderStore, {
      hasPackageGivenPolicyId: 'hasPackageGivenPolicyId',
    }),
    policyStatusIconColor() {
      let color;
      if (this.policyData.terminated) {
        color = 'error';
      } else if (this.policyData.pending) {
        color = '#ee7026';
      } else {
        color = '#0b8d1f';
      }
      return color;
    },
    policyStatusIcon() {
      let icon;
      if (this.policyData.terminated) {
        icon = mdiExclamation;
      } else if (this.policyData.pending) {
        icon = mdiClock;
      } else {
        icon = mdiCheckCircle;
      }
      return icon;
    },
    policyStatusDescription() {
      let description;
      if (this.policyData.terminated) {
        description = this.$t('L_terminated');
      } else if (this.policyData.pending) {
        description = this.$t('L_pending');
      } else {
        description = this.$t('L_active');
      }
      return description;
    },
    hasCRCPackageForCurrentPolicy() {
      return this.hasPackageGivenPolicyId(this.policyData.contractId);
    },
    hasFullPolicyDocuments() {
      if (this.fullPolicyDocument) {
        return true;
      }
      return false;
    },
    fullPolicyDocument() {
      return this.getFullPolicyDocument(this.policyData.contractId);
    },
    policyStatus() {
      return this.policyData.details ? titleCase(this.policyData.details.status) : '';
    },
    formattedCoverageAmount() {
      return currency(this.policyData.totalCoverage, 0);
    },
    showAlert() {
      return this.restrictCodeMessage ? this.restrictCodeMessage.length > 0 : false;
    },
    formattedPrimaryCoverage() {
      return currency(this.policyData.primaryCoverage, 0);
    },
    formattedSpouseCoverage() {
      return currency(this.policyData.spouseCoverage, 0);
    },
    formattedChildrenCoverage() {
      return currency(this.policyData.childCoverage, 0);
    },
    isCanadaPolicy() {
      return this.policyData?.isCanadaPolicy;
    },
    isNewYorkPolicy() {
      return this.policyData?.isNewYorkPolicy;
    },
    formattedPaymentDueDate() {
      return formatDate(this.policyData?.nextPaymentDueDate, 'll');
    },
    spouseName() {
      if (this.policyData?.data?.spouseFirstName) {
        return `${this.policyData?.data?.spouseFirstName} ${this.policyData?.data?.spouseLastName}`;
      }
      return '';
    },
    hasChildren() {
      if (this.policyData?.data?.children && this.policyData?.data?.children.length > 0) {
        return true;
      }
      return false;
    },
    childrenNames() {
      return this.policyData?.data?.children;
    },
  },
  methods: {
    __t(key: string): string {
      return this.$t(key + (this.policyStore.isPending ? '_application' : '')) as string;
    },
    async openDocumentInNewWindow() {
      const url = await this.getAllDocumentsUrl();
      const isWeb = CapacitorFacade.isWeb();
      await this.openInNewWindow({ url, inappbrowser: !isWeb });
    },
    changeStatus() {
      this.status = !this.status;
    },
    isAddAChildren() {
      if (this?.step === 50) {
        this.addChildOpen = true;
      }
    },
    async getAllDocumentsUrl() {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        await this.globalStore.getAllPolicyDocuments(this.fullPolicyDocument!.metadata);
        return URL.createObjectURL(new Blob([this.base64String], { type: 'application/pdf' }));
      } else {
        await this.globalStore.getAllPolicyDocuments(this.fullPolicyDocument!.metadata);
        return this.base64String;
      }
    },
  },
  mounted() {
    this.isAddAChildren();
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.overview-row {
  .overview-info {
    margin-top: 32px;
  }

  @media (min-width: 600px) {
    margin-bottom: 16px;
    margin-top: 16px;

    .overview-info {
      margin-top: 0;
    }
  }
}

.text-decoration--underline {
  text-decoration: underline;
}
.tooltip-overlay.v-tooltip {
  z-index: 2100000000 !important;
  /* Set z-index so the tooltip displays on top of Feedback button on the right edge */
}
</style>
